import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface ProductDetailValuationModalProps {
  open: boolean;
  onClose: () => void;
  propertyPrice?: number;
}

export const ProductDetailValuationModal = ({
  open,
  onClose,
  propertyPrice = 500000,
}: ProductDetailValuationModalProps) => {
  const theme = useTheme();

  const panelSx = {
    background: 'var(--panel-tonal)',
    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
    borderRadius: 2,
  };

  const valuationData = [
    {
      name: 'Low Range',
      value: propertyPrice * 0.9,
      color: '#e89e2a',
      label: '-10%',
    },
    {
      name: 'List Price',
      value: propertyPrice,
      color: '#1d9e75',
      label: 'List Price',
    },
    {
      name: 'High Range',
      value: propertyPrice * 1.1,
      color: '#0a1f44',
      label: '+10%',
    },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'taste-rise taste-panel',
        sx: {
          background: '#ffffff',
          borderRadius: 3,
          boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: '#ffffff',
          px: 3,
          py: 2.5,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            className="font-display"
            sx={{ letterSpacing: '-0.02em', color: theme.palette.text.primary }}
          >
            Property Valuation Report
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
          >
            Comparable-market analysis for this listing
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              background: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            $150
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: theme.palette.text.primary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, background: '#ffffff' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            className="font-display"
            fontWeight={600}
            sx={{ color: theme.palette.text.primary }}
          >
            Valuation Range Analysis
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            Based on market analysis and comparable properties, here&apos;s the
            estimated valuation range:
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          {valuationData.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                flex: 1,
                minWidth: 150,
                p: 2,
                ...panelSx,
                borderColor: alpha(item.color, 0.25),
                background: `linear-gradient(145deg, ${alpha(
                  item.color,
                  0.1
                )} 0%, #ffffff 100%)`,
              }}
            >
              <Typography
                variant="caption"
                display="block"
                sx={{ color: theme.palette.text.secondary }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: theme.palette.text.primary }}
              >
                {formatCurrency(item.value)}
              </Typography>
              {index === 1 && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                >
                  Current List Price
                </Typography>
              )}
            </Paper>
          ))}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            className="font-display"
            fontWeight={600}
            sx={{ color: theme.palette.text.primary }}
          >
            Valuation Distribution
          </Typography>
          <Paper elevation={0} sx={{ p: 2, ...panelSx, background: '#ffffff' }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={valuationData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(theme.palette.text.primary, 0.08)}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                  }}
                  axisLine={{ stroke: alpha(theme.palette.text.primary, 0.15) }}
                  tickLine={{ stroke: alpha(theme.palette.text.primary, 0.15) }}
                />
                <YAxis
                  tick={{
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                  }}
                  axisLine={{ stroke: alpha(theme.palette.text.primary, 0.15) }}
                  tickLine={{ stroke: alpha(theme.palette.text.primary, 0.15) }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  cursor={{ fill: alpha(theme.palette.primary.main, 0.04) }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: `1px solid ${alpha(
                      theme.palette.text.primary,
                      0.12
                    )}`,
                    borderRadius: 8,
                    boxShadow: theme.shadows[4],
                    color: theme.palette.text.primary,
                  }}
                  formatter={
                    ((value: number) => [formatCurrency(value), 'Value']) as any
                  }
                  labelStyle={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                  itemStyle={{ color: theme.palette.text.secondary }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {valuationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Box>

        <Box sx={{ p: 2, ...panelSx, background: '#ffffff' }}>
          <Typography
            variant="h6"
            gutterBottom
            className="font-display"
            fontWeight={600}
            sx={{ color: theme.palette.text.primary }}
          >
            Statistical Analysis
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Standard Deviation
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: theme.palette.text.primary }}
              >
                ±{formatCurrency(propertyPrice * 0.1)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Confidence Level
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: theme.palette.text.primary }}
              >
                95%
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Sample Size
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: theme.palette.text.primary }}
              >
                127 properties
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: '#ffffff',
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          Close
        </Button>
        <Button
          variant="contained"
          sx={{
            fontWeight: 700,
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Purchase Report ($150)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
